import * as React from 'react';

// Constants & Enums
import { ErrorCode } from '@tob/shared/api-constants';
import { AppleAuthProvider } from './constants';
import { AuthMethod } from '@shopee/web-bridge-sdk';

// Utils
import { isWebBridgeSupported } from '@shopee-user/platform';
import type { WithUserWebViewProps } from '@shopee-user/with-web-view';
import { addAppleScript } from '@tob/shared/thirdparty-auth/auth-provider/connect-with-apple/utils';
import { createEncryptedState } from '@tob/shared/security/oauth-state';
import { getWebThirdPartyClientId } from '@shopee-user/business-customization-logic';
import { AuthenticateChannel } from '@tob/shared/thirdparty-auth/core/types';
import {
  ThirdpartyBindingEntryPoint,
  THIRDPARTY_AUTH_PROVIDER_REDIRECT_URI,
} from '@tob/shared/thirdparty-auth/service/constants';

export const useConnectWithApple = ({
  webViewAuthWith3PServiceByToken,
  entryPoint,
}: {
  webViewAuthWith3PServiceByToken?: WithUserWebViewProps['webViewAuthWith3PServiceByToken'];
  entryPoint: ThirdpartyBindingEntryPoint;
}) => {
  const [isReady, setIsReady] = React.useState(false);

  const webClientId = getWebThirdPartyClientId({
    channel: AuthenticateChannel.Apple,
  });

  React.useEffect(() => {
    let mounted = true;
    /**
     * Only initialize SDK in web usecase
     */
    if (!isWebBridgeSupported()) {
      (async () => {
        try {
          await addAppleScript();
          const AppleID = (window as any)?.AppleID;
          if (!AppleID?.auth?.init) {
            // If SDK didn't expose init, we still allow sign in path to try
            if (mounted) setIsReady(true);
            return;
          }

          // Create an encrypted state for Apple
          // This mirrors how Google flow constructs state and can be used for CSRF protection
          let state: string | undefined = undefined;
          try {
            const encrypted = await createEncryptedState({
              type: AuthenticateChannel.Apple,
            });
            state = encrypted.state;
          } catch {
            // ignore state generation errors; proceed without it
          }

          AppleID.auth.init({
            clientId: webClientId,
            scope: 'name email',
            redirectURI: THIRDPARTY_AUTH_PROVIDER_REDIRECT_URI[entryPoint],
            state,
            usePopup: true,
          });
          if (mounted) setIsReady(true);
        } catch {
          if (mounted) setIsReady(false);
        }
      })();
    } else {
      setIsReady(true);
    }

    return () => {
      mounted = false;
    };
  }, [webClientId]);

  const authenticate = async (): Promise<{
    authProvider: AppleAuthProvider;
    idToken?: string;
    appClientId?: string;
    isCancel?: boolean;
    error?: number;
    errorMsg?: string;
  }> => {
    if (isWebBridgeSupported()) {
      const result = await authenticateByAppleApp();
      return {
        ...result,
        authProvider: AppleAuthProvider.AppleApp,
      };
    } else {
      const result = await authenticateByAppleWeb();
      return {
        ...result,
        authProvider: AppleAuthProvider.AppleWeb,
      };
    }
  };

  const authenticateByAppleApp = async (): Promise<{
    idToken?: string;
    appClientId?: string;
    isCancel?: boolean;
    error?: number;
    errorMsg?: string;
  }> => {
    if (!webViewAuthWith3PServiceByToken) {
      return {
        error: 1,
        errorMsg: 'webViewAuthWith3PServiceByToken is not available',
      };
    }

    try {
      const response = await webViewAuthWith3PServiceByToken({
        method: AuthMethod.Apple,
      });

      if (response.errorCode !== 0) {
        return {
          error: response.errorCode,
          errorMsg: response.errorMessage,
        };
      }

      if (response.authToken) {
        return {
          idToken: response.authToken,
          appClientId: response.serverClientId,
        };
      }

      return { isCancel: true };
    } catch (err) {
      return {
        error: ErrorCode.FeErrorCode,
        errorMsg: err instanceof Error ? err.message : String(err),
      };
    }
  };

  const authenticateByAppleWeb = async (): Promise<{
    idToken?: string;
    isCancel?: boolean;
    error?: number;
    errorMsg?: string;
  }> => {
    try {
      await addAppleScript();
      const AppleID = (window as any)?.AppleID;
      if (!AppleID?.auth?.signIn) {
        return {
          error: ErrorCode.FeErrorCode,
          errorMsg: 'AppleID SDK not available',
        };
      }
      const { authorization } = await AppleID.auth.signIn();
      const { id_token: idToken } = authorization || {};
      if (idToken) {
        return { idToken };
      }
      return { isCancel: true };
    } catch (err) {
      const errObj =
        typeof err === 'object' && err !== null
          ? (err as Record<string, unknown>)
          : undefined;
      const message =
        (typeof err === 'string' && err) ||
        (typeof errObj?.error === 'string' && errObj.error) ||
        (typeof errObj?.message === 'string' && errObj.message) ||
        String(err);
      if (
        typeof message === 'string' &&
        message.toLowerCase().includes('cancel')
      ) {
        return { isCancel: true };
      }
      return {
        error: ErrorCode.FeErrorCode,
        errorMsg: `Apple sign in failed: ${message}`,
      };
    }
  };

  return {
    authenticate,
    isReady,
  };
};
